import React, { useCallback, useEffect, useRef, useState } from 'react';
import cc from 'classcat';

import styles from './styles.module.scss';

interface NavItem {
  id: string;
  label: string;
}

interface Props {
  items: NavItem[];
}

const MenorahNav = ({ items }: Props) => {
  const [activeId, setActiveId] = useState<string>('');
  const [hidden, setHidden] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const rafRef = useRef<number>(0);
  const prevActiveIndexRef = useRef<number>(-1);

  const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Measure the fixed header and set nav top to sit flush beneath it
  useEffect(() => {
    const updateNavTop = () => {
      const header = document.querySelector('header');
      if (!header || !navRef.current) return;

      const skinny = header.querySelector('[class*="skinny"]') as HTMLElement;
      if (skinny) {
        const cs = getComputedStyle(skinny);
        if (cs.position === 'fixed' && cs.display !== 'none') {
          navRef.current.style.top = `${skinny.offsetHeight}px`;
          return;
        }
      }

      navRef.current.style.top = `${header.offsetHeight}px`;
    };

    updateNavTop();
    window.addEventListener('scroll', updateNavTop, { passive: true });
    window.addEventListener('resize', updateNavTop, { passive: true });

    return () => {
      window.removeEventListener('scroll', updateNavTop);
      window.removeEventListener('resize', updateNavTop);
    };
  }, []);

  // Scroll-position based active section tracking
  useEffect(() => {
    const getStickyOffset = () => {
      const header = document.querySelector('header') as HTMLElement;
      if (!header) return 0;

      const skinny = header.querySelector('[class*="skinny"]') as HTMLElement;
      if (skinny) {
        const cs = getComputedStyle(skinny);
        if (cs.position === 'fixed' && cs.display !== 'none') {
          return skinny.offsetHeight;
        }
      }
      return header.offsetHeight;
    };

    // Set active id and update fill direction based on forward/backward movement
    const updateActiveId = (id: string) => {
      const newIndex = items.findIndex((item) => item.id === id);
      const prevIndex = prevActiveIndexRef.current;
      if (newIndex !== prevIndex && navRef.current) {
        const origin = prevIndex === -1 || newIndex > prevIndex ? 'left center' : 'right center';
        navRef.current.style.setProperty('--fill-origin', origin);
      }
      prevActiveIndexRef.current = newIndex;
      setActiveId(id);
    };

    const updateActiveSection = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const stickyOffset = getStickyOffset();
      const navHeight = navRef.current?.offsetHeight || 54;
      const referencePoint = scrollTop + stickyOffset + navHeight + 20;

      // Hide nav when footer is reached
      const footer = document.querySelector('footer');
      if (footer) {
        const footerTop = footer.getBoundingClientRect().top;
        setHidden(footerTop <= stickyOffset + navHeight + 10);
      }

      // Check if at page bottom — activate last section
      const pageHeight = document.documentElement.scrollHeight;
      if (scrollTop + window.innerHeight >= pageHeight - 50) {
        const lastId = items[items.length - 1]?.id || '';
        updateActiveId(lastId);
        return;
      }

      // Find the section whose top is closest to (but above) the reference point
      let active = '';
      for (const { id } of items) {
        const el = document.getElementById(id);
        if (!el) continue;

        const rect = el.getBoundingClientRect();
        const sectionTop = rect.top + scrollTop;

        if (sectionTop <= referencePoint) {
          active = id;
        } else {
          break;
        }
      }

      updateActiveId(active || items[0]?.id || '');
    };

    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateActiveSection);
    };

    updateActiveSection();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, [items]);

  // Auto-scroll the nav bar so the active link is visible on mobile
  useEffect(() => {
    if (!activeId || !listRef.current) return;
    const activeLink = listRef.current.querySelector(`[href="#${activeId}"]`) as HTMLElement;
    if (activeLink) {
      const list = listRef.current;
      const scrollLeft = activeLink.offsetLeft - 16;
      list.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
  }, [activeId]);

  return (
    <nav ref={navRef} className={cc({ [styles.nav]: true, [styles.hidden]: hidden })}>
      <div className="container">
        <ul ref={listRef} className={styles.list}>
          {items.map(({ id, label }) => (
            <li key={id} className={styles.item}>
              <a
                href={`#${id}`}
                className={cc({ [styles.link]: true, [styles.active]: activeId === id })}
                onClick={(e) => handleClick(e, id)}
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default MenorahNav;
